// Size picker modal. Legacy: toggleSizeModal/filterSizes/filterSizeCategory/
// applyCustomSize/sizePickerImageDesign (snapshot L1472-1530, L3327-3689).
// Rebuilt 1-to-1 on the live design via ui/dom.ts openModal(): a search input,
// main tabs (All / Label Sheets / Label Printers / Custom / Image), brand/shape
// sub-tabs, a scrollable list of .size-card rows from LABEL_SIZES + SHEET_LAYOUTS,
// a custom W/H form, and a "design on image" dropzone.
import type { LabelSize, SheetLayout } from './../types'
import { toMm, fmtUnit, customName, type Unit } from './../units'
import { state } from './../state'
import { LABEL_SIZES, SHEET_LAYOUTS, MM_TO_PX, sheetToLabelSize, resolveCatalogueId } from './../constants'
import { resizeCanvas, fitToView } from './../canvas'
import { updateStatus } from './../statusbar'
import { addImageFromFile } from './../widgets/image'
import { el, openModal, closeModal } from './../ui/dom'
import { icon } from './../ui/icons'
import { refreshHeader } from './../ui/toolbar'
import { onSelectionChange } from './properties'
import { searchCatalogue, shapeOf, SHAPE_OPTIONS } from './../search'

/** Apply a single label size (clears any sheet selection). */
export function applySize(size: LabelSize): void {
  state.currentSheet = null
  state.currentSize = size
  // Classic parity (snapshot L3616-3618): DYMO/Brother die-cut rolls have rounded
  // corners — reflect that on the canvas; other sizes reset the toggle.
  state.roundedCorners = /^(dymo|brother)$/i.test(size.brand ?? '') && size.category !== 'tape' && size.category !== 'continuous'
  // Document pages (A4/Letter invoices) read naturally in portrait; labels keep
  // their canonical orientation (width >= height ⇒ landscape).
  state.orientation = size.category === 'document' ? 'portrait'
    : size.widthMM >= size.heightMM ? 'landscape' : 'portrait'
  resizeCanvas()
  fitToView()
  updateStatus()
  refreshHeader()
  onSelectionChange() // refresh the (label) properties panel to the new size
}

/** Apply a multi-up sheet layout (canvas shows one cell). */
export function applySheet(sheetLayout: SheetLayout, opts: { query?: string } = {}): void {
  state.currentSheet = sheetLayout
  state.currentSize = sheetToLabelSize(sheetLayout)
  // Classic parity (snapshot L3620-3629): picking a rounded-corner SKU (…R codes /
  // roundedCodes) turns the rounded-corner preview on; other picks reset it.
  const q = (opts.query ?? '').trim().toUpperCase()
  state.roundedCorners = !!q && (
    (sheetLayout.roundedCodes ?? []).some((c) => c.toUpperCase() === q) ||
    (sheetLayout.codes ?? []).some((c) => c.toUpperCase() === q && /R$/.test(c.toUpperCase()))
  )
  state.orientation = sheetLayout.labelWidthMM >= sheetLayout.labelHeightMM ? 'landscape' : 'portrait'
  resizeCanvas()
  fitToView()
  updateStatus()
  refreshHeader()
  onSelectionChange() // refresh the (label) properties panel to the new size
}

/** Apply by catalogue id — size ids, classic aliases, sheet ids and classic
 *  'sheet-<id>' forms (marketing deep links + saved designs use all of these). */
export function applySizeById(id: string): boolean {
  const hit = resolveCatalogueId(id)
  if (!hit) return false
  if (hit.sheet) applySheet(hit.sheet)
  else applySize(hit.size)
  return true
}

export function closeSizePicker(): void {
  closeModal()
}

type MainTab = 'all' | 'sheets' | 'printers' | 'custom' | 'image'

/** Distinct printer brands present in LABEL_SIZES (in first-seen order). */
function printerBrands(): string[] {
  const out: string[] = []
  for (const s of LABEL_SIZES) {
    const b = s.brand ?? 'Generic'
    if (s.category === 'custom') continue
    if (!out.includes(b)) out.push(b)
  }
  return out
}

/** Shape filter options (shared with the start screen). */
function shapeOptions(): { id: string; label: string }[] { return SHAPE_OPTIONS }

export function openSizePicker(): void {
  let mainTab: MainTab = 'all'
  let brandFilter = 'all'
  let shapeFilter = 'all'
  let query = ''

  // ---- search ----
  const search = el('input', 'prop-input w-full mb-4') as HTMLInputElement
  search.type = 'text'
  search.placeholder = 'Search all sizes (e.g. L7160, DYMO, 21/sheet, 63.5)...'

  // ---- main tabs ----
  const mainTabsRow = el('div', 'flex gap-2 mb-3')
  const mainTabDefs: { id: MainTab; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'sheets', label: 'Label Sheets' },
    { id: 'printers', label: 'Label Printers' },
    { id: 'custom', label: '✏️ Custom' },
    { id: 'image', label: '🖼️ Image' },
  ]
  const mainTabBtns = new Map<MainTab, HTMLElement>()
  for (const def of mainTabDefs) {
    const b = el('button', 'size-tab size-main-tab text-xs px-3 py-1.5 rounded-full font-medium', def.label)
    b.addEventListener('click', () => { mainTab = def.id; render() })
    mainTabBtns.set(def.id, b)
    mainTabsRow.append(b)
  }

  // ---- shape + brand sub-tabs ----
  const shapeTabs = el('div', 'flex gap-1.5 mb-2 flex-wrap')
  const brandTabs = el('div', 'flex gap-1.5 mb-4 flex-wrap')

  // ---- scrollable list ----
  const list = el('div', 'space-y-2 max-h-[50vh] overflow-y-auto pr-2')

  // ---- custom form ----
  const customForm = el('div', 'mt-4 p-4 rounded-lg border border-slate-600')
  {
    customForm.append(el('div', 'text-sm font-medium text-white mb-3', 'Custom Size'))
    const row = el('div', 'flex gap-3 items-end')
    let wLbl: HTMLElement, hLbl: HTMLElement
    let unit: Unit = 'mm'
    const wWrap = el('div')
    wWrap.append((wLbl = el('label', 'text-[10px] text-slate-500', 'Width (mm)')))
    const wIn = el('input', 'prop-input') as HTMLInputElement
    wIn.type = 'number'; wIn.value = '89'; wIn.min = '10'; wIn.max = '500'
    wWrap.append(wIn)
    const hWrap = el('div')
    hWrap.append((hLbl = el('label', 'text-[10px] text-slate-500', 'Height (mm)')))
    const hIn = el('input', 'prop-input') as HTMLInputElement
    hIn.type = 'number'; hIn.value = '36'; hIn.min = '10'; hIn.max = '500'
    hWrap.append(hIn)
    const apply = el('button', 'tb-btn primary', 'Apply')
    apply.addEventListener('click', () => {
      const w = clampMm(toMm(parseFloat(wIn.value), unit))
      const h = clampMm(toMm(parseFloat(hIn.value), unit))
      applySize({ id: 'custom', name: customName(w, h, unit), brand: 'Custom', widthMM: w, heightMM: h, shape: 'rect', category: 'custom' })
      closeSizePicker()
    })
    // mm / inch toggle: inputs convert in place; sizes are stored in mm.
    const unitWrap = el('div')
    unitWrap.append(el('label', 'text-[10px] text-slate-500', 'Unit'))
    const unitSel = el('select', 'prop-input') as HTMLSelectElement
    for (const u of ['mm', 'in'] as Unit[]) { const o = el('option') as HTMLOptionElement; o.value = u; o.textContent = u === 'mm' ? 'mm' : 'inches'; unitSel.appendChild(o) }
    unitSel.addEventListener('change', () => {
      const next = unitSel.value as Unit
      const wMm = toMm(parseFloat(wIn.value) || 0, unit), hMm = toMm(parseFloat(hIn.value) || 0, unit)
      unit = next
      wIn.value = fmtUnit(wMm, unit); hIn.value = fmtUnit(hMm, unit)
      wIn.step = hIn.step = unit === 'in' ? '0.125' : '1'
      wIn.min = hIn.min = unit === 'in' ? '0.4' : '10'; wIn.max = hIn.max = unit === 'in' ? '20' : '500'
      wLbl.textContent = `Width (${unit})`; hLbl.textContent = `Height (${unit})`
    })
    unitWrap.append(unitSel)
    row.append(wWrap, hWrap, unitWrap, apply)
    customForm.append(row)
  }

  // ---- image form ----
  const imageForm = el('div', 'mt-4 p-4 rounded-lg border border-slate-600')
  {
    imageForm.append(el('div', 'text-sm font-medium text-white mb-3', 'Design on Image'))
    const drop = el('div', 'border-2 border-dashed border-slate-600 rounded-xl p-5 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-500/5 transition-all')
    const ic = el('div', 'text-slate-500 flex justify-center mb-2')
    ic.innerHTML = icon('image', 'w-7 h-7')
    drop.append(ic)
    drop.append(el('div', 'text-sm text-slate-300 font-medium mb-1', 'Drop an image or click to browse'))
    drop.append(el('div', 'text-xs text-slate-500', 'Canvas will resize to match the image'))
    const fileInput = el('input', 'hidden') as HTMLInputElement
    fileInput.type = 'file'; fileInput.accept = 'image/*'
    drop.addEventListener('click', () => fileInput.click())
    drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('border-blue-500', 'bg-blue-500/5') })
    drop.addEventListener('dragleave', () => drop.classList.remove('border-blue-500', 'bg-blue-500/5'))
    drop.addEventListener('drop', (e) => {
      e.preventDefault()
      drop.classList.remove('border-blue-500', 'bg-blue-500/5')
      const f = (e as DragEvent).dataTransfer?.files?.[0]
      if (f) void designOnImage(f)
    })
    fileInput.addEventListener('change', () => {
      const f = fileInput.files?.[0]
      if (f) void designOnImage(f)
    })
    imageForm.append(drop, fileInput)
  }

  // ---- rendering ----
  const sizeCard = (name: string, sub: string, active: boolean, onClick: () => void): HTMLElement => {
    const card = el('div', 'size-card' + (active ? ' active' : ''))
    const left = el('div')
    left.append(el('div', 'text-sm font-medium text-white', name))
    left.append(el('div', 'text-xs text-slate-400', sub))
    card.append(left)
    card.addEventListener('click', onClick)
    return card
  }


  const renderShapeTabs = () => {
    shapeTabs.replaceChildren()
    const show = mainTab === 'sheets' || mainTab === 'printers'
    shapeTabs.classList.toggle('hidden', !show)
    if (!show) return
    for (const s of shapeOptions()) {
      const b = el('button', 'start-size-btn', s.label)
      if (s.id === shapeFilter) { b.style.background = 'rgba(37,99,235,0.2)'; b.style.borderColor = '#2563eb'; b.style.color = '#e2e8f0' }
      b.addEventListener('click', () => { shapeFilter = s.id; render() })
      shapeTabs.append(b)
    }
  }

  const renderBrandTabs = () => {
    brandTabs.replaceChildren()
    const show = mainTab === 'sheets' || mainTab === 'printers'
    brandTabs.classList.toggle('hidden', !show)
    if (!show) return
    const brands = mainTab === 'sheets'
      ? ['all', ...distinct(SHEET_LAYOUTS.map((s) => s.brand ?? 'Avery'))]
      : ['all', ...printerBrands()]
    for (const br of brands) {
      const b = el('button', 'start-size-btn', br === 'all' ? 'All' : br)
      if (br.toLowerCase() === brandFilter.toLowerCase()) { b.style.background = 'rgba(37,99,235,0.2)'; b.style.borderColor = '#2563eb'; b.style.color = '#e2e8f0' }
      b.addEventListener('click', () => { brandFilter = br; render() })
      brandTabs.append(b)
    }
  }

  const renderList = () => {
    list.replaceChildren()
    list.classList.toggle('hidden', mainTab === 'custom' || mainTab === 'image')
    if (mainTab === 'custom' || mainTab === 'image') return

    let any = false
    const showSizes = mainTab === 'all' || mainTab === 'printers'
    const showSheets = mainTab === 'all' || mainTab === 'sheets'

    if (showSizes) {
      const sizes = searchCatalogue(query, LABEL_SIZES.filter((s) => {
        if (s.category === 'custom') return false
        if (brandFilter !== 'all' && (s.brand ?? '').toLowerCase() !== brandFilter.toLowerCase()) return false
        if (shapeFilter !== 'all' && shapeOf(s) !== shapeFilter) return false
        return true
      }))
      if (sizes.length) {
        if (mainTab === 'all') list.append(el('h3', 'text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1', 'Label Printers'))
        for (const sz of sizes) {
          const active = !state.currentSheet && state.currentSize.id === sz.id
          list.append(sizeCard(sz.name, `${sz.brand} · ${sz.widthMM}×${sz.heightMM} mm`, active, () => { applySize(sz); closeSizePicker() }))
        }
        any = true
      }
    }

    if (showSheets) {
      const sheets = searchCatalogue(query, SHEET_LAYOUTS.filter((s) => {
        if (brandFilter !== 'all' && (s.brand ?? 'Avery').toLowerCase() !== brandFilter.toLowerCase() && !(s.notes ?? '').toLowerCase().includes(brandFilter.toLowerCase())) return false
        if (shapeFilter !== 'all' && shapeOf(s) !== shapeFilter) return false
        return true
      }))
      if (sheets.length) {
        if (mainTab === 'all') list.append(el('h3', 'text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1', 'Label Sheets'))
        for (const sh of sheets) {
          const active = state.currentSize.id === sh.id
          const codeStr = (sh.codes ?? []).slice(0, 3).join(' / ')
          list.append(sizeCard(sh.name, `${sh.brand}${codeStr ? ` ${codeStr}` : ''} · ${sh.columns}×${sh.rows} on ${sh.pageWidthMM}×${sh.pageHeightMM} mm`, active, () => { applySheet(sh, { query }); closeSizePicker() }))
        }
        any = true
      }
    }

    if (!any) list.append(el('div', 'text-sm text-slate-500 text-center py-6', 'No matches.'))
  }

  const render = () => {
    for (const [id, b] of mainTabBtns) {
      const isActive = id === mainTab
      b.className = 'size-tab size-main-tab text-xs px-3 py-1.5 rounded-full font-medium ' +
        (isActive ? 'bg-brand text-white' : 'bg-slate-700 text-slate-300')
    }
    renderShapeTabs()
    renderBrandTabs()
    renderList()
    customForm.classList.toggle('hidden', mainTab !== 'custom')
    imageForm.classList.toggle('hidden', mainTab !== 'image')
  }

  search.addEventListener('input', () => { query = search.value; renderList() })

  openModal({
    title: 'Choose Label Size',
    width: '700px',
    testid: 'size-picker',
    body: [search, mainTabsRow, shapeTabs, brandTabs, list, customForm, imageForm],
  })
  render()
  search.focus()
}

function clampMm(v: number): number {
  if (Number.isNaN(v)) return 50
  return Math.max(10, Math.min(500, v))
}

function distinct(arr: string[]): string[] {
  const out: string[] = []
  for (const x of arr) if (!out.includes(x)) out.push(x)
  return out
}

/** Resize the canvas to match an image's dimensions, then place the image. */
async function designOnImage(file: File): Promise<void> {
  closeSizePicker()
  try {
    const dims = await imageDimsMm(file)
    applySize({ id: 'custom', name: `Image ${dims.w}×${dims.h} mm`, brand: 'Custom', widthMM: dims.w, heightMM: dims.h, shape: 'rect', category: 'custom' })
  } catch {
    /* fall through — add at current size */
  }
  await addImageFromFile(file)
}

/** Read an image file's natural pixel size and convert to mm @96dpi. */
function imageDimsMm(file: File): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      const w = clampMm(Math.round(img.naturalWidth / MM_TO_PX))
      const h = clampMm(Math.round(img.naturalHeight / MM_TO_PX))
      resolve({ w, h })
    }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')) }
    img.src = url
  })
}
